<template>
  <header v-if="identity" class="header">
    <div class="logo" @click="goHome()">
      <div v-dompurify-html:svg="logo" class="logo__string"></div>
      <span class="logo__title">{{ identity.contact.organization }}</span>
    </div>
    <OpeningHours class="ms-auto ms-md-4 me-md-auto" />
    <nav class="nav-links align-items-center">
      <div v-if="identity.maintenanceMode" class="text-warning">
        <Popper content="Mode maintenance activé" hover arrow placement="bottom" theme="light">
          <AppIcon icon="maintenance" />
        </Popper>
      </div>
      <LanguageSwitch v-if="canSwitchLanguage" />
      <a :href="identity?.contact.website.url" target="_blank" class="d-none d-md-block">
        {{ $t('header.custom_website_link_label') }}
      </a>
    </nav>
  </header>
</template>

<script lang="ts">
import { PropType, defineComponent, nextTick } from 'vue'
import { IdentityData } from '~concierge/types/IdentityTypes'
import OpeningHours from '~conciergeUiCommons/components/OpeningHours.vue'
import { router, usePage } from '@inertiajs/vue3'
import { useAppointmentStore } from '~conciergeUiCommons/store/appointmentStore'
import { useSubmissionFormStore } from '~conciergeUiCommons/store/submissionFormStore'
import LanguageSwitch from '~conciergeUiCommons/components/LanguageSwitch.vue'

export default defineComponent({
  name: 'HeaderSection',
  components: { OpeningHours, LanguageSwitch },
  props: {
    identity: {
      type: Object as PropType<IdentityData>,
      required: true
    },
    imageSize: {
      type: String,
      default: '30px'
    },
    fillColor: {
      type: String,
      default: '#fff'
    }
  },
  setup() {
    const page = usePage()
    const appointmentStore = useAppointmentStore()
    const submissionFormStore = useSubmissionFormStore()
    const resetStore = () => {
      appointmentStore.$reset()
      submissionFormStore.$reset()
    }
    return {
      resetStore,
      submissionFormStore,
      appointmentStore,
      page
    }
  },
  computed: {
    logo() {
      return this.identity?.logoUrl ?? ''
    },
    canSwitchLanguage() {
      return this.identity?.canSwitchLanguage
    }
  },
  methods: {
    async goHome(): Promise<void> {
      this.resetStore()
      await nextTick()
      const homeUrl = this.page.props.homeUrl
      if (typeof homeUrl === 'string') {
        router.visit(homeUrl)
      }
    }
  }
})
</script>

<style lang="scss">
@use '~concierge/styles/bootstrap' as *;

body.desktop .header {
  border-top-left-radius: var(--vff-border-radius, 4px);
  border-top-right-radius: var(--vff-border-radius, 4px);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5em 1em;
  background-color: var(--dealer-header-background);
  color: var(--dealer-header-text);
}

.logo {
  cursor: pointer;
  display: inline-flex;
  place-items: center;
}

.logo__string {
  --size: 4rem;
  height: var(--size);
  width: var(--size);
  display: grid;
  place-content: center;
  place-items: center;
}

.logo__string svg {
  height: 100%;
  width: 100%;
  fill: var(--dealer-header-text);
}

.logo__title {
  padding-left: 1.5rem;
}

.nav-links {
  display: flex;
  gap: 1rem;
}

.nav-links {
  a {
    @include rfs(1.2rem);
    font-weight: 500;
    color: var(--dealer-header-link);

    &.router-link-exact-active {
      color: var(--dealer-header-link);
    }
  }
}
</style>
